import type { FloorEdgeOverridesTable } from "./floorAssemblyTypes";
import type { BayEdgeInheritedProps } from "./floorBayTypes";
import { getBaySplitEdgeSourceId, isSplitLineEdge, parseSplitLineEdgeId } from "./floorBayTypes";
import type { BayFramingEdgeOverridesTable, FloorBay, FloorSplitData } from "./floorSplitDataTypes";

/**
 * Merges bay-level edge overrides with parent floor edge overrides.
 * Bay overrides take precedence. For bay-split edges, the parent floor
 * override is looked up via the source edge ID.
 */
export function mergeBayEdgeOverrides(
  bayEdgeIds: string[],
  bayOverrides: FloorEdgeOverridesTable | undefined,
  parentFloorOverrides: FloorEdgeOverridesTable | undefined,
): FloorEdgeOverridesTable {
  const merged: Record<string, { readonly framingConfig?: { readonly count: number } }> = {};
  for (const edgeId of bayEdgeIds) {
    const bayOverride = bayOverrides?.[edgeId];
    if (bayOverride?.framingConfig) {
      merged[edgeId] = bayOverride;
      continue;
    }
    const sourceId = getBaySplitEdgeSourceId(edgeId);
    const lookupId = sourceId ?? edgeId;
    const parentOverride = parentFloorOverrides?.[lookupId];
    if (parentOverride) {
      merged[edgeId] = parentOverride;
    }
  }
  return merged;
}

/**
 * Resolves the inherited and overridden properties for a bay edge.
 * Returns the parent floor edge override (inherited), the bay-level override,
 * and the effective framing config after merging.
 *
 * For split-line-derived edges (IDs starting with "splitline-"), inherited properties
 * are limited to what the split line edge provides (no offset or foundation), and
 * `isSplitLineDerived` is set to true.
 */
export function resolveBayEdgeInheritedProps(
  bayEdgeId: string,
  parentFloorEdgeOverrides: FloorEdgeOverridesTable | undefined,
  bayEdgeOverrides: BayFramingEdgeOverridesTable | undefined,
  splitData?: FloorSplitData,
): BayEdgeInheritedProps {
  const overridden = bayEdgeOverrides?.[bayEdgeId] ?? {};

  if (isSplitLineEdge(bayEdgeId) && splitData) {
    const parsed = parseSplitLineEdgeId(bayEdgeId);
    const splitLine = parsed ? splitData.splitLines?.[parsed.splitLineId] : undefined;
    const splitEdgeId = splitLine?.edgeIds?.[parsed?.edgeIndex ?? -1];
    const splitEdgeOverride = splitEdgeId ? splitLine?.edgeOverrides?.[splitEdgeId] : undefined;

    const inherited: FloorEdgeOverridesTable[string] =
      splitEdgeOverride?.thickness !== undefined ? { framingConfig: { count: 1 } } : {};

    const effectiveFramingConfig = overridden.framingConfig ?? inherited.framingConfig;

    return {
      inherited,
      overridden,
      effective: { framingConfig: effectiveFramingConfig },
      isSplitLineDerived: true,
    };
  }

  const sourceId = getBaySplitEdgeSourceId(bayEdgeId);
  const lookupId = sourceId ?? bayEdgeId;
  const inherited = parentFloorEdgeOverrides?.[lookupId] ?? {};

  const effectiveFramingConfig = overridden.framingConfig ?? inherited.framingConfig;

  return {
    inherited,
    overridden,
    effective: { framingConfig: effectiveFramingConfig },
    isSplitLineDerived: false,
  };
}

export function resolveBayFramingField<K extends keyof FloorBay>(
  bay: FloorBay | undefined,
  floorSystemValue: FloorBay[K] | undefined,
  field: K,
): FloorBay[K] | undefined {
  if (bay && bay[field] !== undefined) {
    return bay[field];
  }
  return floorSystemValue;
}

export function resolveBayEdgeOverride(
  edgeId: string,
  bayOverrides: FloorEdgeOverridesTable | undefined,
  floorOverrides: FloorEdgeOverridesTable | undefined,
): FloorEdgeOverridesTable[string] | undefined {
  if (bayOverrides?.[edgeId]) {
    return bayOverrides[edgeId];
  }
  const sourceId = getBaySplitEdgeSourceId(edgeId);
  const lookupId = sourceId ?? edgeId;
  return floorOverrides?.[lookupId];
}
